import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { GRACKLE_COLORS, SEMANTIC, ACTIVITY_TYPE_COLORS } from '@/lib/theme'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useCreateCallLog } from '@/hooks/use-api'
import { useCallProvider } from './CallProvider'
import { Phone, PhoneOff, Clock, User } from 'lucide-react'
import type { CallDisposition, CallDirection } from '@/types'
import { explainError } from '@/lib/explain-error'

const DISPOSITION_OPTIONS: { value: CallDisposition; label: string }[] = [
  { value: 'Connected', label: 'Connected' },
  { value: 'No Answer', label: 'No Answer' },
  { value: 'Left Voicemail', label: 'Left Voicemail' },
  { value: 'Busy', label: 'Busy' },
  { value: 'Wrong Number', label: 'Wrong Number' },
  { value: 'Disconnected', label: 'Disconnected' },
  { value: 'Gatekeeper', label: 'Gatekeeper' },
]

const DIRECTION_OPTIONS: { value: CallDirection; label: string }[] = [
  { value: 'Outbound', label: 'Outbound' },
  { value: 'Inbound', label: 'Inbound' },
]

function formatDuration(seconds: number): string {
  const hrs = Math.floor(seconds / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

export function CallLogDialog() {
  const { toast } = useToast()
  const createCallLog = useCreateCallLog()
  const {
    activeCall,
    endCall,
    cancelCall,
    isCallDialogOpen,
    closeCallDialog,
    callDialogContext,
    callDialogDefaults,
  } = useCallProvider()

  // Form state
  const [direction, setDirection] = useState<CallDirection>('Outbound')
  const [disposition, setDisposition] = useState<CallDisposition>('Connected')
  const [subject, setSubject] = useState('')
  const [notes, setNotes] = useState('')
  const [manualDuration, setManualDuration] = useState('')

  // Reset form when dialog opens
  useEffect(() => {
    if (isCallDialogOpen && callDialogDefaults) {
      setDirection((callDialogDefaults.direction as CallDirection) || 'Outbound')
      setDisposition((callDialogDefaults.disposition as CallDisposition) || 'Connected')
      setSubject('')
      setNotes('')
      setManualDuration('')
    }
  }, [isCallDialogOpen, callDialogDefaults])

  const entity = callDialogContext?.entity
  const contactName = entity?.who_name || 'Unknown'
  const phoneNumber = entity?.phone_number || ''

  const handleEndCallAndLog = async () => {
    const callResult = endCall()

    try {
      const durationSeconds = callResult
        ? callResult.duration
        : manualDuration
          ? parseInt(manualDuration) * 60
          : 0

      await createCallLog.mutateAsync({
        direction,
        phone_number: phoneNumber,
        formatted_phone: entity?.formatted_phone,
        duration_seconds: durationSeconds,
        started_at: callResult?.startedAt || new Date().toISOString(),
        ended_at: callResult?.endedAt || new Date().toISOString(),
        disposition,
        subject: subject || `${direction} call - ${contactName}`,
        notes: notes || undefined,
        who_id: entity?.who_id,
        who_type: entity?.who_type,
        what_id: entity?.what_id,
        what_type: entity?.what_type,
      })

      toast({
        title: 'Call Logged',
        description: `Call with ${contactName} logged successfully.`,
      })

      closeCallDialog()
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to log call'),
        variant: 'destructive',
      })
    }
  }

  const handleLogWithoutTimer = async () => {
    try {
      const durationSeconds = manualDuration ? parseInt(manualDuration) * 60 : 0

      await createCallLog.mutateAsync({
        direction,
        phone_number: phoneNumber,
        formatted_phone: entity?.formatted_phone,
        duration_seconds: durationSeconds,
        started_at: new Date().toISOString(),
        ended_at: new Date().toISOString(),
        disposition,
        subject: subject || `${direction} call - ${contactName}`,
        notes: notes || undefined,
        who_id: entity?.who_id,
        who_type: entity?.who_type,
        what_id: entity?.what_id,
        what_type: entity?.what_type,
      })

      toast({
        title: 'Call Logged',
        description: `Call with ${contactName} logged successfully.`,
      })

      closeCallDialog()
    } catch (error) {
      toast({
        title: 'Error',
        description: explainError(error, 'Failed to log call'),
        variant: 'destructive',
      })
    }
  }

  const handleCancel = () => {
    cancelCall()
    closeCallDialog()
  }

  return (
    <Dialog open={isCallDialogOpen} onOpenChange={(open) => !open && handleCancel()}>
      <DialogContent className="max-w-md" aria-describedby="call-log-description">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone
              className="h-5 w-5"
              aria-hidden="true"
              style={{ color: ACTIVITY_TYPE_COLORS.Call.solid }}
            />
            Log Call
          </DialogTitle>
          <DialogDescription id="call-log-description">
            {activeCall.isActive
              ? 'Call in progress. Fill in details and end the call when done.'
              : 'Log the details of this call.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Contact Info */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-md">
            <User className="h-5 w-5 text-muted-foreground flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-medium">{contactName}</p>
              <p className="text-sm text-muted-foreground">{phoneNumber}</p>
            </div>
          </div>

          {/* Active Call Timer */}
          {activeCall.isActive && (
            <div
              className="flex items-center justify-between p-3 rounded-md border"
              role="timer"
              aria-live="polite"
              aria-label={`Call duration: ${formatDuration(activeCall.elapsedSeconds)}`}
              style={{
                backgroundColor: SEMANTIC.success.fill,
                borderColor: GRACKLE_COLORS.prussianBlue,
              }}
            >
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 rounded-full animate-pulse"
                  aria-hidden="true"
                  style={{ backgroundColor: GRACKLE_COLORS.navy }}
                />
                <span className="text-sm font-medium" style={{ color: SEMANTIC.success.text }}>
                  Call in progress
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: SEMANTIC.success.text }}
                />
                <span
                  className="font-mono text-lg font-bold"
                  style={{ color: SEMANTIC.success.text }}
                >
                  {formatDuration(activeCall.elapsedSeconds)}
                </span>
              </div>
            </div>
          )}

          {/* Direction */}
          <div>
            <Label htmlFor="call-direction">Direction</Label>
            <Select value={direction} onValueChange={(v) => setDirection(v as CallDirection)}>
              <SelectTrigger id="call-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DIRECTION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Disposition */}
          <div>
            <Label htmlFor="call-disposition">Disposition</Label>
            <Select value={disposition} onValueChange={(v) => setDisposition(v as CallDisposition)}>
              <SelectTrigger id="call-disposition">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {DISPOSITION_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Manual Duration (only when timer not active) */}
          {!activeCall.isActive && (
            <div>
              <Label htmlFor="call-duration">Duration (minutes)</Label>
              <Input
                id="call-duration"
                type="number"
                min="0"
                value={manualDuration}
                onChange={(e) => setManualDuration(e.target.value)}
                placeholder="Enter call duration in minutes"
              />
            </div>
          )}

          {/* Subject */}
          <div>
            <Label htmlFor="call-subject">Subject</Label>
            <Input
              id="call-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={`${direction} call - ${contactName}`}
            />
          </div>

          {/* Notes */}
          <div>
            <Label htmlFor="call-notes">Notes</Label>
            <Textarea
              id="call-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Call notes..."
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
            {activeCall.isActive ? (
              <Button
                onClick={handleEndCallAndLog}
                disabled={createCallLog.isPending}
                style={{
                  backgroundColor: GRACKLE_COLORS.darkRaspberry,
                  color: '#ffffff',
                }}
                className="hover:opacity-90"
              >
                {createCallLog.isPending ? (
                  'Saving...'
                ) : (
                  <>
                    <PhoneOff className="h-4 w-4 mr-2" aria-hidden="true" />
                    End Call & Log
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleLogWithoutTimer}
                disabled={createCallLog.isPending}
                style={{ backgroundColor: GRACKLE_COLORS.navy, color: '#ffffff' }}
                className="hover:opacity-90"
              >
                {createCallLog.isPending ? 'Saving...' : 'Log Call'}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
